// ── Welle B2: Klassenarbeit als Liste ────────────────────────────────────────────
// EIN Fenster für die ganze App (am App-Root eingehängt), geöffnet über
// oeffneKlassenarbeitListe() aus der Aufgabe in Mein Tag, dem Kalendereintrag und dem
// Notenrechner. Alle Kinder der Klasse mit je einem Notenfeld; Datum, Fach und Art oben
// einmal. Eine gültige Note (1–6) springt sofort zum nächsten Kind, Enter ebenso.
// Gespeichert wird über dieselben Endpunkte wie einzelne Noten (POST/PUT/DELETE /api/noten,
// Halbjahr nach Datum) — die Rechnung bleibt unverändert.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiFetch, apiGet, apiPost } from '../../lib/api';
import { toast } from '../../lib/toast';
import { notenFarbeKMK } from '../../lib/utils';
import { KA_LISTE_EVENT, KaListeZiel } from '../../lib/nav';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdSegmented } from '../../components/ld/LdForm';
import { zaehltText } from '../../lib/noten-zaehlt';
import './KlassenarbeitListe.css';
import { LdAuswahl, LdDatum } from '../../components/ld/LdFenster';

type NoteRow = { id: number; schueler_id: number; note: number; gewichtung: number; kommentar: string | null; datum: string };
type Kind = { id: number; vorname: string; nachname: string; note: NoteRow | null; weitere: number };
type Liste = { termin_id: number | null; klasse_id: number; klasse_name: string | null; fach: string | null; faecher: string[]; datum: string; thema: string | null; kinder: Kind[] };
type Zeile = { wert: string; fehlt: boolean; bemerkung: string; bemerkungOffen: boolean };

const leer = (k: Kind): Zeile => ({
  wert: k.note ? String(Math.round(k.note.note)) : '', fehlt: false,
  bemerkung: k.note?.kommentar ?? '', bemerkungOffen: !!k.note?.kommentar,
});

export function KlassenarbeitListeHost() {
  const [ziel, setZiel] = useState<KaListeZiel | null>(null);
  useEffect(() => {
    const h = (e: Event) => setZiel((e as CustomEvent<KaListeZiel>).detail ?? null);
    window.addEventListener(KA_LISTE_EVENT, h);
    return () => window.removeEventListener(KA_LISTE_EVENT, h);
  }, []);
  return <KlassenarbeitListe ziel={ziel} onClose={() => setZiel(null)} />;
}

function KlassenarbeitListe({ ziel, onClose }: { ziel: KaListeZiel | null; onClose: () => void }) {
  const [liste, setListe] = useState<Liste | null>(null);
  const [fehler, setFehler] = useState('');
  const [datum, setDatum] = useState('');
  const [fach, setFach] = useState('');
  const [gewicht, setGewicht] = useState(1);
  const [zeilen, setZeilen] = useState<Record<number, Zeile>>({});
  const [speichert, setSpeichert] = useState(false);
  const [fehlgeschlagen, setFehlgeschlagen] = useState<Record<number, string>>({});
  const felder = useRef<(HTMLInputElement | null)[]>([]);

  const laden = useCallback(async (z: KaListeZiel, d?: string, f?: string) => {
    setFehler(''); setFehlgeschlagen({});
    const q = new URLSearchParams();
    if (z.terminId) q.set('termin_id', String(z.terminId));
    if (z.klasseId) q.set('klasse_id', String(z.klasseId));
    if (f ?? z.fach) q.set('fach', String(f ?? z.fach));
    if (d) q.set('datum', d);
    try {
      const l = await apiGet<Liste>(`/api/klassenarbeit/liste?${q}`);
      setListe(l); setDatum(l.datum); setFach(l.fach ?? '');
      setZeilen(Object.fromEntries(l.kinder.map(k => [k.id, leer(k)])));
      setGewicht(l.kinder.find(k => k.note)?.note?.gewichtung ?? 1);
    } catch (err: any) { setFehler(err?.message ?? 'Die Liste konnte nicht geladen werden.'); }
  }, []);

  useEffect(() => {
    if (!ziel) { setListe(null); setFehler(''); return; }
    laden(ziel);
  }, [ziel, laden]);

  const zuFeld = (i: number) => { const el = felder.current[i]; if (el) { el.focus(); el.select(); } };
  const setze = (id: number, z: Partial<Zeile>) => setZeilen(alt => ({ ...alt, [id]: { ...alt[id], ...z } }));

  function eingabe(i: number, k: Kind, roh: string) {
    const ziffer = roh.replace(/[^1-6]/g, '').slice(-1);
    setze(k.id, { wert: ziffer, fehlt: false });
    if (ziffer) zuFeld(i + 1);
  }

  // Was beim Speichern passiert (je Kind höchstens ein Aufruf)
  type Schritt = { k: Kind; art: 'neu' | 'aendern' | 'loeschen'; note?: number; kommentar: string | null };
  function schritte(): Schritt[] {
    if (!liste) return [];
    const aus: Schritt[] = [];
    for (const k of liste.kinder) {
      const z = zeilen[k.id]; if (!z) continue;
      const kommentar = z.bemerkung.trim() || null;
      const n = z.fehlt ? null : Number(z.wert) || null;
      if (k.note) {
        if (n == null) aus.push({ k, art: 'loeschen', kommentar });
        else if (n !== Math.round(k.note.note) || kommentar !== (k.note.kommentar ?? null) || gewicht !== k.note.gewichtung || datum !== k.note.datum.slice(0, 10))
          aus.push({ k, art: 'aendern', note: n, kommentar });
      } else if (n != null) aus.push({ k, art: 'neu', note: n, kommentar });
    }
    return aus;
  }
  const plan = schritte();
  const anzahl = { neu: plan.filter(s => s.art === 'neu').length, aendern: plan.filter(s => s.art === 'aendern').length, loeschen: plan.filter(s => s.art === 'loeschen').length };
  const eingetragen = liste ? liste.kinder.filter(k => !zeilen[k.id]?.fehlt && zeilen[k.id]?.wert).length : 0;
  const fehlen = liste ? liste.kinder.filter(k => zeilen[k.id]?.fehlt).length : 0;

  async function speichern() {
    if (!liste || !fach) return;
    setSpeichert(true);
    const fehl: Record<number, string> = {};
    for (const s of plan) {
      try {
        if (s.art === 'neu') await apiPost('/api/noten', {
          schueler_id: s.k.id, klasse_id: liste.klasse_id, fach, art: 'KA', note: s.note, gewichtung: gewicht, datum, kommentar: s.kommentar,
        });
        else if (s.art === 'aendern') await apiFetch(`/api/noten/${s.k.note!.id}`, {
          method: 'PUT', body: JSON.stringify({ art: 'KA', note: s.note, gewichtung: gewicht, datum, kommentar: s.kommentar }),
        });
        else await apiFetch(`/api/noten/${s.k.note!.id}`, { method: 'DELETE' });
      } catch (err: any) { fehl[s.k.id] = err?.message ?? 'Nicht gespeichert.'; }
    }
    setFehlgeschlagen(fehl);
    setSpeichert(false);
    const n = Object.keys(fehl).length;
    if (n) { toast(`${n} ${n === 1 ? 'Note wurde' : 'Noten wurden'} nicht gespeichert – bitte prüfen.`, 'error'); return; }
    if (liste.termin_id) { try { await apiPost(`/api/klassenarbeit/${liste.termin_id}/erledigt`); } catch { /* Aufgabe bleibt offen */ } }
    toast(plan.length ? `Klassenarbeit gespeichert · ${eingetragen} Noten` : 'Keine Änderungen', 'success');
    window.dispatchEvent(new CustomEvent('ld-noten-geaendert'));
    onClose();
  }

  const titel = liste ? `Klassenarbeit ${fach || ''} ${liste.klasse_name ?? ''}`.replace(/\s+/g, ' ').trim() : 'Klassenarbeit eintragen';
  return (
    <LdSheet open={!!ziel} onClose={onClose} maxWidth={620}
      kopf={{ titel, kontext: liste ? `${liste.thema ? `${liste.thema}, ` : ''}${liste.kinder.length} Kinder` : null }}>
      <div className="ld-ka">
        {fehler ? <div className="ld-pu-fehler" role="alert"><span>{fehler}</span></div>
          : !liste ? <div className="ld-pu-laedt"><span /><span /></div>
          : (<>
            <div className="ld-ka__oben">
              <label className="ld-ka__feld"><span>Datum</span>
                <LdDatum value={datum} onChange={e => { setDatum(e.target.value); if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value) && ziel) laden({ ...ziel, terminId: null, klasseId: liste.klasse_id }, e.target.value, fach); }} /></label>
              <label className="ld-ka__feld"><span>Fach</span>
                {liste.faecher.length > 1 || !liste.fach
                  ? <LdAuswahl value={fach} onChange={e => { setFach(e.target.value); if (ziel) laden({ ...ziel, terminId: null, klasseId: liste.klasse_id }, datum, e.target.value); }}>
                      <option value="" disabled>Fach wählen</option>
                      {liste.faecher.map(f => <option key={f} value={f}>{f}</option>)}
                    </LdAuswahl>
                  : <b>{fach}</b>}</label>
              <div className="ld-ka__feld"><span>Art</span><b>Klassenarbeit</b></div>
              <div className="ld-ka__feld ld-ka__feld--breit"><span>Gewichtung</span>
                <LdSegmented value={gewicht} onChange={setGewicht} options={[0.5, 1, 1.5, 2].map(g => ({ value: g, label: `×${g}` }))} />
                <small>{zaehltText('KA', gewicht)}</small></div>
            </div>
            <ol className="ld-ka__liste">
              {liste.kinder.map((k, i) => {
                const z = zeilen[k.id] ?? leer(k);
                const wert = Number(z.wert);
                return (
                  <li key={k.id} className={`ld-ka__kind${z.fehlt ? ' ist-fehlt' : ''}${fehlgeschlagen[k.id] ? ' ist-fehler' : ''}`}>
                    <div className="ld-ka__name">
                      <b>{k.nachname}, {k.vorname}</b>
                      {k.note && <small>{z.fehlt || !z.wert ? 'Note wird gelöscht' : `eingetragen: ${Math.round(k.note.note)}`}{k.weitere ? ` · ${k.weitere} weitere am selben Tag` : ''}</small>}
                      {fehlgeschlagen[k.id] && <small className="ld-ka__fehler" role="alert">{fehlgeschlagen[k.id]}</small>}
                    </div>
                    <input ref={el => { felder.current[i] = el; }} className="ld-ka__note" aria-label={`Note für ${k.vorname} ${k.nachname}`}
                      inputMode="numeric" pattern="[1-6]" autoComplete="off" enterKeyHint={i === liste.kinder.length - 1 ? 'done' : 'next'}
                      value={z.fehlt ? '' : z.wert} disabled={z.fehlt} placeholder="–"
                      style={wert ? { color: notenFarbeKMK(wert), borderColor: notenFarbeKMK(wert) } : undefined}
                      onChange={e => eingabe(i, k, e.target.value)}
                      onFocus={e => e.target.select()}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); zuFeld(i + 1); } }} />
                    <button type="button" className={`ld-ka__fehlt${z.fehlt ? ' an' : ''}`} aria-pressed={z.fehlt} onClick={() => setze(k.id, { fehlt: !z.fehlt })}>fehlt</button>
                    <button type="button" className="ld-ka__bem-knopf" aria-label={`Bemerkung zu ${k.vorname}`} aria-expanded={z.bemerkungOffen}
                      onClick={() => setze(k.id, { bemerkungOffen: !z.bemerkungOffen })}><MIcon name={z.bemerkung ? 'sticky_note_2' : 'add_comment'} size={18} /></button>
                    {z.bemerkungOffen && (
                      <input className="ld-ka__bem" value={z.bemerkung} placeholder="Bemerkung (optional)" aria-label={`Bemerkung zu ${k.vorname}`}
                        onChange={e => setze(k.id, { bemerkung: e.target.value })} />
                    )}
                  </li>
                );
              })}
            </ol>
            <div className="ld-ka__fuss">
              <span className="ld-ka__stand">{eingetragen} von {liste.kinder.length} eingetragen{fehlen ? ` · ${fehlen} fehlt` : ''}{anzahl.loeschen ? ` · ${anzahl.loeschen} wird gelöscht` : ''}</span>
              <button type="button" className="ld-ka__speichern" onClick={speichern} disabled={speichert || !fach || !datum}>
                <MIcon name="check" size={18} />{speichert ? 'Speichern …' : 'Alle speichern'}</button>
            </div>
          </>)}
      </div>
    </LdSheet>
  );
}
